import { useState, useEffect, useRef } from "react";
import { X, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

interface Category {
  id: string;
  name: string;
  color_code?: string;
}

interface CategoryModalProps {
  isOpen: boolean;
  mode: "add" | "edit";
  category?: Category;
  onClose: () => void;
  onSave: (data: { name: string; slug: string; color_code?: string }) => void;
  saving?: boolean;
  serverErrors?: { name?: string; colorCode?: string };
}

export const CategoryModal = ({
  isOpen,
  mode,
  category,
  onClose,
  onSave,
  saving = false,
  serverErrors = {},
}: CategoryModalProps) => {
  const [name, setName] = useState("");
  const [colorCode, setColorCode] = useState("#885ABB");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; colorCode?: string }>({});
  const colorPickerRef = useRef<HTMLDivElement>(null);

  // Preset colors for quick selection
  const presetColors = [
    "#885ABB", "#40C4E7", "#4CCFC1", "#7E9AEF", "#C49AF3",
    "#F59E0B", "#10B981", "#EF4444", "#6366F1", "#EC4899",
    "#8B5CF6", "#06B6D4", "#14B8A6", "#F97316", "#84CC16"
  ];

  useEffect(() => {
    if (isOpen) {
      setName(category?.name || "");
      setColorCode(category?.color_code || "#885ABB");
      setErrors({});
    }
  }, [isOpen, category]);

  // Close color picker when clicking outside
  useEffect(() => {
    if (!showColorPicker) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (colorPickerRef.current && !colorPickerRef.current.contains(target)) {
        // Check if the click is on the color preview button (which should toggle)
        const isColorButton = (target as HTMLElement).closest('button[title="Select color"]');
        if (!isColorButton) {
          setShowColorPicker(false);
        }
      }
    };

    // Use capture phase and add a small delay to avoid immediate closing
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [showColorPicker]);

  const validate = () => {
    const newErrors: { name?: string; colorCode?: string } = {};
    if (!name.trim()) {
      newErrors.name = "Category name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    const hexRegex = /^#([A-Fa-f0-9]{3}){1,2}$/;
    if (!colorCode) {
      newErrors.colorCode = "Color code is required";
    } else if (!hexRegex.test(colorCode)) {
      newErrors.colorCode = "Please enter a valid hex color (e.g., #885ABB)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Generate slug from name
      const slug = name.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      onSave({ name: name.trim(), slug: slug, color_code: colorCode });
      setName("");
      setColorCode("#885ABB");
      setErrors({});
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-[1000]">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
        {/* Standardized Close Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onClose}
          className="absolute lg:-left-12 right-4 top-4 lg:right-auto lg:top-6 w-11 h-11 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-md hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
          aria-label="Close"
        >
          <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
        </motion.button>

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === "add" ? "Add New Category" : "Edit Category"}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="categoryName"
              className="text-sm font-medium text-gray-700"
            >
              Category Name *
            </label>
            <input
              id="categoryName"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
              }}
              placeholder="e.g. Hair Care, Skin Care"
              className={`w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB] transition-colors ${(errors.name || serverErrors.name) ? "border-red-500 bg-red-50" : "border-gray-300"
                }`}
              autoFocus
            />
            {(errors.name || serverErrors.name) && (
              <p className="text-xs text-red-500 mt-1">{errors.name || serverErrors.name}</p>
            )}
          </div>

          {/* Color Code Field */}
          <div className="space-y-2">
            <label
              htmlFor="colorCode"
              className="text-sm font-medium text-gray-700"
            >
              Color Code
            </label>
            <div className="relative" ref={colorPickerRef}>
              <div className="flex items-center gap-3">
                {/* Color Preview Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowColorPicker(!showColorPicker);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-sm hover:border-[#885ABB] transition-colors flex-shrink-0"
                  style={{ backgroundColor: colorCode }}
                  title="Select color"
                >
                  <span className="sr-only">Color picker</span>
                </button>

                {/* Color Input */}
                <div className="flex-1 relative">
                  <input
                    id="colorCode"
                    type="text"
                    value={colorCode}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^#?[0-9A-Fa-f]{0,6}$/.test(value) || value === "") {
                        setColorCode(value.startsWith("#") ? value : `#${value}`);
                        if (errors.colorCode) setErrors(prev => ({ ...prev, colorCode: undefined }));
                      }
                    }}
                    placeholder="#885ABB"
                    className={`w-full rounded-xl border px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB] font-mono text-sm transition-colors ${(errors.colorCode || serverErrors.colorCode) ? "border-red-500 bg-red-50" : "border-gray-300"
                      }`}
                  />
                  {(errors.colorCode || serverErrors.colorCode) && (
                    <p className="text-xs text-red-500 mt-1">{errors.colorCode || serverErrors.colorCode}</p>
                  )}
                  <input
                    type="color"
                    value={colorCode}
                    onChange={(e) => setColorCode(e.target.value)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded border border-gray-300 cursor-pointer opacity-0 pointer-events-none"
                  />
                </div>
              </div>

              {/* Color Picker Dropdown */}
              {showColorPicker && (
                <div
                  className="absolute z-10 mt-2 p-4 bg-white rounded-xl shadow-lg border border-gray-200 w-full"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="space-y-3">
                    {/* Native Color Picker */}
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-medium text-gray-600 flex-shrink-0">
                        Custom Color:
                      </label>
                      <input
                        type="color"
                        value={colorCode}
                        onChange={(e) => {
                          e.stopPropagation();
                          setColorCode(e.target.value);
                          // Don't close immediately - let user continue selecting
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full h-10 rounded-lg border border-gray-300 cursor-pointer"
                      />
                    </div>

                    {/* Preset Colors */}
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-2">
                        Preset Colors:
                      </label>
                      <div className="grid grid-cols-5 gap-2">
                        {presetColors.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setColorCode(color);
                              // Don't close immediately, let user see the selection
                              setTimeout(() => {
                                setShowColorPicker(false);
                              }, 150);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className={`w-full h-10 rounded-lg border-2 transition-all hover:scale-110 ${colorCode === color
                              ? "border-gray-800 shadow-md ring-2 ring-offset-2 ring-[#885ABB]"
                              : "border-gray-300 hover:border-gray-400"
                              }`}
                            style={{ backgroundColor: color }}
                            title={color}
                          >
                            <span className="sr-only">{color}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Choose a color to represent this category
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 rounded-xl bg-[#885ABB] hover:bg-[#6f47a2] text-white transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] justify-center"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{mode === "add" ? "Adding..." : "Saving..."}</span>
                </>
              ) : (
                <span>{mode === "add" ? "Add Category" : "Save Changes"}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
